import { useMemo, type CSSProperties } from "react";
import { useAppSelector } from "./hooks";
import { RootState } from "../redux/store";
import {
    resolveBranding,
    DEFAULT_BRANDING_COLORS,
    type OrgBranding,
    type OrgBrandingInput,
} from "../utils/orgBranding";

export interface AssessmentTheme {
    /** "branded" when org branding is active, else "default". */
    variant: "default" | "branded";
    primary: string;
    accent: string;
    gradient: string;
    meshGradient: string;
    textOnGradient: string;
    shadowTinted: string;
    /** Class applied to a wrapping container. */
    cssClass: string;
    /** CSS custom properties to spread into the wrapper's `style`. Drives the
     *  `--p3m-*` variables used across assessment styling, fully data-driven. */
    cssVars: CSSProperties;
}

/** Convert #rrggbb to rgba() with the given alpha. Falls back to the hex as-is. */
function hexToRgba(hex: string, alpha: number): string {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
    if (!m) return hex;
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildTheme(branding: OrgBranding | null): AssessmentTheme {
    const colors = branding?.colors || DEFAULT_BRANDING_COLORS;
    const primary = colors.primaryDark || colors.primary;
    const accent = colors.accent;
    const gradient = `linear-gradient(135deg, ${primary} 0%, ${colors.primary} 100%)`;
    const meshGradient = `radial-gradient(at 20% 0%, ${hexToRgba(
        colors.primary,
        0.2
    )}, transparent 50%), radial-gradient(at 100% 100%, ${hexToRgba(
        accent,
        0.13
    )}, transparent 60%)`;
    const textOnGradient = colors.textOnPrimary || "#ffffff";
    const shadowTinted = `0 8px 24px ${hexToRgba(colors.primary, 0.2)}`;

    return {
        variant: branding ? "branded" : "default",
        primary,
        accent,
        gradient,
        meshGradient,
        textOnGradient,
        shadowTinted,
        cssClass: branding ? "assessment-theme-branded" : "assessment-theme-default",
        cssVars: {
            "--p3m-primary": primary,
            "--p3m-accent": accent,
            "--p3m-gradient": gradient,
            "--p3m-mesh": meshGradient,
            "--p3m-text-on-gradient": textOnGradient,
            "--p3m-shadow-tinted": shadowTinted,
            "--p3m-tinted-soft": hexToRgba(colors.primary, 0.08),
        } as CSSProperties,
    };
}

export const getAssessmentTheme = (
    org?: OrgBrandingInput | null
): AssessmentTheme => buildTheme(resolveBranding(org));

export const useAssessmentTheme = (): AssessmentTheme => {
    const selectedOrganization = useAppSelector(
        (state: RootState) => state.organization.selectedOrganization
    );

    return useMemo(
        () => getAssessmentTheme(selectedOrganization),
        [selectedOrganization?.name, selectedOrganization?.customization]
    );
};

export default useAssessmentTheme;
